import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Monitor, Download, Upload, Lock, Clock, QrCode, 
  Mail, BarChart3, Shield, Crown, ArrowRight, 
  CheckCircle, Zap, FileArchive, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';

const SCREENSHOTS = {
  login: '/images/logiciel/login.jpg',
  upload: '/images/logiciel/upload.jpg',
  share: '/images/logiciel/share.jpg',
};

const FEATURES = [
  { icon: Upload, title: 'Upload par glisser-deposer', desc: 'Deposez vos fichiers directement dans le logiciel, sans ouvrir le navigateur.' },
  { icon: Lock, title: 'Protection par mot de passe', desc: 'Securisez vos partages avec un mot de passe comme sur le site.' },
  { icon: Clock, title: 'Expiration personnalisable', desc: 'Definissez la duree de validite de chaque lien de partage.' },
  { icon: QrCode, title: 'QR Code instantane', desc: 'Generez un QR Code pour chaque partage en un clic.' },
  { icon: Mail, title: 'Envoi par email', desc: 'Partagez directement par email depuis le logiciel.' },
  { icon: BarChart3, title: 'Statistiques', desc: 'Suivez les telechargements de vos fichiers en temps reel.' },
  { icon: FileArchive, title: 'Compression ZIP auto', desc: 'Les fichiers multiples sont compresses automatiquement.' },
  { icon: Shield, title: 'Chiffrement E2E', desc: 'Vos fichiers sont chiffres de bout en bout, stockes en France.' },
];

const STEPS = [
  { num: '01', title: 'Telechargez', desc: 'Installez SENDBOX pour Windows en quelques secondes.', img: SCREENSHOTS.login },
  { num: '02', title: 'Connectez-vous', desc: 'Utilisez vos identifiants Pro pour acceder au service.', img: SCREENSHOTS.login },
  { num: '03', title: 'Envoyez', desc: 'Glissez vos fichiers et configurez le partage en un clic.', img: SCREENSHOTS.upload },
  { num: '04', title: 'Partagez', desc: 'Copiez le lien, scannez le QR code ou envoyez par email.', img: SCREENSHOTS.share },
];

const LogicielPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <NavigationMenu />
      <main className="pt-24 pb-32">

        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-300 text-sm mb-6">
            <Crown className="w-4 h-4 text-yellow-400" />
            Exclusif Pro
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="logiciel-title">
            SENDBOX pour
            <br />
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Windows
            </span>
          </h1>
          <p className="text-lg md:text-xl text-blue-200/70 max-w-3xl mx-auto mb-10">
            Toute la puissance de SENDBOX directement sur votre bureau.
            Envoyez vos fichiers sans ouvrir le navigateur, avec toutes les fonctionnalites Pro.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <a href="/downloads/sendbox-setup.exe" download>
              <Button size="lg" className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-lg px-8 py-6" data-testid="download-exe-btn">
                <Download className="w-5 h-5 mr-2" /> Telecharger pour Windows
              </Button>
            </a>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10 text-lg px-8 py-6">
                Passer Pro <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
          <p className="text-xs text-blue-300/40">Windows 10/11 • Version 1.0 • 67 Mo • Necessite un compte Pro</p>

          {/* Hero screenshot */}
          <div className="mt-12 max-w-4xl mx-auto">
            <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-2 shadow-2xl shadow-black/40">
              <img 
                src={SCREENSHOTS.upload} 
                alt="SENDBOX Desktop - Interface principale" 
                className="w-full rounded-xl"
                data-testid="logiciel-hero-screenshot"
              />
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="max-w-6xl mx-auto px-6 mb-24">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Toutes les fonctionnalites Pro</h2>
          <p className="text-blue-200/60 text-center mb-12 max-w-2xl mx-auto">
            Retrouvez exactement les memes fonctionnalites que sur le site, dans une interface desktop optimisee.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f, i) => (
              <div key={i} className="bg-slate-800/30 border border-blue-500/10 rounded-xl p-5 hover:border-cyan-500/30 transition-all group">
                <div className="w-10 h-10 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center justify-center mb-3 group-hover:bg-cyan-500/10 group-hover:border-cyan-500/20 transition-colors">
                  <f.icon className="w-5 h-5 text-blue-400 group-hover:text-cyan-400 transition-colors" />
                </div>
                <h3 className="text-white font-semibold text-sm mb-1">{f.title}</h3>
                <p className="text-blue-200/50 text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How it works - Steps */}
        <section className="max-w-5xl mx-auto px-6 mb-24">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Comment ca marche</h2>
          <div className="space-y-16">
            {STEPS.map((step, i) => (
              <div key={i} className={`flex flex-col ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8`}>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-4xl font-black bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">{step.num}</span>
                    <h3 className="text-2xl font-bold text-white">{step.title}</h3>
                  </div>
                  <p className="text-blue-200/60 text-base leading-relaxed">{step.desc}</p>
                </div>
                <div className="flex-1 max-w-md">
                  <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-1.5 shadow-xl">
                    <img src={step.img} alt={step.title} className="w-full rounded-lg" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Comparison: Web vs Desktop */}
        <section className="max-w-4xl mx-auto px-6 mb-24">
          <h2 className="text-3xl font-bold text-white text-center mb-10">Web vs Desktop</h2>
          <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl overflow-hidden">
            <table className="w-full" data-testid="logiciel-comparison">
              <thead>
                <tr className="border-b border-blue-500/20">
                  <th className="text-left p-4 text-blue-300/70 font-medium">Fonctionnalite</th>
                  <th className="text-center p-4 text-white font-medium">Site Web</th>
                  <th className="text-center p-4 text-cyan-400 font-medium flex items-center justify-center gap-2">
                    <Monitor className="w-4 h-4" /> Logiciel
                  </th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {[
                  ['Upload de fichiers', '✓', '✓'],
                  ['Protection mot de passe', '✓', '✓'],
                  ['Expiration personnalisable', '✓', '✓'],
                  ['QR Code', '✓', '✓'],
                  ['Envoi par email', '✓', '✓'],
                  ['Statistiques', '✓', '✓'],
                  ['Chiffrement E2E', '✓', '✓'],
                  ['Stockage France / Europe', '✓', '✓'],
                  ['Acces sans navigateur', '—', '✓'],
                  ['Glisser-deposer natif', '—', '✓'],
                  ['Demarrage rapide', '—', '✓'],
                ].map((row, idx) => (
                  <tr key={idx} className="border-b border-blue-500/10 last:border-0">
                    <td className="p-4 text-blue-200/80">{row[0]}</td>
                    <td className="p-4 text-center text-blue-300/60">{row[1]}</td>
                    <td className="p-4 text-center text-cyan-300">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* France Storage Banner */}
        <section className="max-w-4xl mx-auto px-6 mb-20">
          <div className="bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border border-blue-500/20 rounded-2xl p-8 flex items-center gap-6">
            <div className="flex-shrink-0 w-16 h-16 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center justify-center">
              <Shield className="w-8 h-8 text-blue-400" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-1">Meme securite, meme hebergement</h3>
              <p className="text-blue-200/70 text-sm leading-relaxed">
                Le logiciel utilise exactement les memes serveurs que le site web. Vos fichiers sont stockes en France et en Europe, conformes au RGPD.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-cyan-500/30 rounded-3xl p-10">
            <Crown className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">Pret a essayer ?</h2>
            <p className="text-blue-200/70 mb-8 max-w-xl mx-auto">
              Telechargez SENDBOX pour Windows et commencez a envoyer vos fichiers directement depuis votre bureau.
              Necesssite un abonnement Pro.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/downloads/sendbox-setup.exe" download>
                <Button size="lg" className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600">
                  <Download className="w-5 h-5 mr-2" /> Telecharger gratuitement
                </Button>
              </a>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10">
                  Decouvrir l'offre Pro <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

      </main>
      <DynamicFooter fixed={true} />
    </div>
  );
};

export default LogicielPage;
